import { num, pct } from "../../lib/format";
import type { CorrelationX, CorrelationY } from "../../lib/demographics";

/** A measure the cross-section can be plotted against, and how to print it. */
export interface PlotMetric<T extends string> {
  id: T;
  /** Shown in the picker. */
  label: string;
  /** Shown on the axis and in the sentence under the chart. */
  axis: string;
  format: (v: number) => string;
}

/**
 * The demographic side of the scatter.
 *
 * All measured over the most recent intercensal period, or as a stock at the
 * latest census — the same numbers the map is shaded by, so a shape seen on
 * the map can be checked against prices without changing units in your head.
 */
export const X_METRICS: PlotMetric<CorrelationX>[] = [
  { id: "growth_rate", label: "Population", axis: "population change, %/yr", format: pct },
  { id: "migration_rate", label: "Migration", axis: "net migration, %/yr", format: pct },
  { id: "natural_rate", label: "Natural", axis: "natural change, %/yr", format: pct },
  {
    id: "household_growth",
    label: "Households",
    axis: "household growth, %/yr",
    format: pct,
  },
  {
    id: "density",
    label: "Density",
    axis: "people per km²",
    format: (v) => num(Math.round(v)),
  },
  {
    id: "vacancy_rate",
    label: "Vacancy",
    axis: "vacant homes, % of stock",
    format: (v) => `${(v * 100).toFixed(1)}%`,
  },
  {
    id: "second_rate",
    label: "Second homes",
    axis: "second homes, % of stock",
    format: (v) => `${(v * 100).toFixed(1)}%`,
  },
];

/**
 * The price side.
 *
 * Level and trend answer different questions and often disagree: the expensive
 * communes in this region are mostly the ones losing residents, while the ones
 * gaining residents are cheaper and rising faster.
 */
export const Y_METRICS: PlotMetric<CorrelationY>[] = [
  { id: "price_growth", label: "Price trend", axis: "price trend, %/yr", format: pct },
  {
    id: "price_m2",
    label: "Price level",
    axis: "€/m²",
    format: (v) => `${num(Math.round(v))} €`,
  },
];

export const xMetric = (id: CorrelationX) => X_METRICS.find((m) => m.id === id)!;
export const yMetric = (id: CorrelationY) => Y_METRICS.find((m) => m.id === id)!;
